import { fetchLocalization, type StudioLocalizationResponse } from './studioApi'

export type RuntimeLocalization = StudioLocalizationResponse

export function normalizeLocale(value: unknown): string {
	const parts = String(value || '')
		.trim()
		.replace(/_/g, '-')
		.split('-')
		.filter(Boolean)
	return parts
		.map((part, index) => {
			if (index === 0) return part.toLowerCase()
			if (/^[a-z]{4}$/i.test(part)) {
				return `${part[0].toUpperCase()}${part.slice(1).toLowerCase()}`
			}
			if (/^[a-z]{2}$/i.test(part) || /^\d{3}$/.test(part)) {
				return part.toUpperCase()
			}
			return part.toLowerCase()
		})
		.join('-')
}

export async function loadLocalization(
	locale = ''
): Promise<RuntimeLocalization> {
	try {
		return await fetchLocalization(locale)
	} catch (error) {
		const fallback = normalizeLocale(locale) || 'en'
		console.warn(
			`[localization] Live catalogue unavailable; using bundled copy: ${
				error instanceof Error ? error.message : String(error)
			}`
		)
		return {
			locale: fallback,
			default_locale: fallback,
			supported_locales: [fallback],
			direction: ['ar', 'ckb', 'dv', 'fa', 'he', 'ku', 'ps', 'ur'].includes(
				fallback.split('-')[0]
			)
				? 'rtl'
				: 'ltr',
			messages: {},
			resources: {},
			// Regional formatting is deliberately omitted here: the live values
			// are unknown in this branch, and inventing 'en-GB'/'GBP' defaults is
			// how the storefront ended up printing '£' at a Saudi tenant.
			// Consumers treat absent as "use plain locale-driven Intl".
		}
	}
}

export function requestedLocale(input: {
	locale?: string
	query?: Record<string, unknown>
}): string {
	const queryLocale = input.query?.lang
	return normalizeLocale(
		(typeof queryLocale === 'string' ? queryLocale : '') || input.locale || 'en'
	)
}

/** Attach the resolved tenant locale to a same-origin API route without
 * making callers care whether the route already has query parameters. */
export function withLocale(path: string, locale: string): string {
	if (!locale) return path
	const separator = path.includes('?') ? '&' : '?'
	return `${path}${separator}locale=${encodeURIComponent(locale)}`
}
